import { c as _c } from "react-compiler-runtime";
import { createFocusManager } from "@react-aria/focus";
import { useEvent } from "@react-aria/utils";
import React from "react";
import { mergeProps, useDateField, useDatePicker, useDateSegment, useLocale, useObjectRef } from "react-aria";
import { Button as AriaButton, ButtonContext, Dialog, Group, Input as AriaInput, LabelContext, ListBoxContext, ListStateContext, OverlayTriggerStateContext, Provider, useSlottedContext } from "react-aria-components";
import { useDatePickerState } from "react-stately";
import { useMediaLayout } from "use-media";
import { Calendar as LineCalendar } from "../icons/line.js";
import { Calendar as SolidCalendar } from "../icons/solid.js";
import { mediaQueryMax } from "../vars.js";
import * as styles from "./DatePicker.css.js";
import { ListBox } from "./ListBox.js";
import { ListBoxItem } from "./ListBoxItem.js";
import { Popover } from "./Popover.js";
import { Spinner } from "./Spinner.js";
import { Tab, TabList, TabPanel, Tabs } from "./Tabs.js";
import { FCTextFieldContext } from "./TextFieldContext.js";
import useI18n from "./internal/useI18n.js";
import { useMonthYearDateFieldState } from "./internal/useMonthYearDateFieldState.js";
import { InputBuilder } from "./shared/InputBuilder.js";
import { extractInputBuilderProps } from "./shared/inputBuilderHelper.js";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const BaseCalendarButton = (t0, ref) => {
  const $ = _c(10);
  const {
    visible: t1,
    excludeFromTabOrder
  } = t0;
  const visible = t1 === undefined ? true : t1;
  const state = React.useContext(OverlayTriggerStateContext);
  const isOpen = state?.isOpen ?? false;
  const isSmallScreen = useMediaLayout(mediaQueryMax.xxs);
  if (isSmallScreen) {
    return null;
  }
  let t2;
  if ($[0] !== isOpen || $[1] !== visible) {
    t2 = styles.calendarButton({
      isOpen,
      visible
    });
    $[0] = isOpen;
    $[1] = visible;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== isOpen) {
    t3 = t4 => {
      const {
        isFocused
      } = t4;
      return isFocused || isOpen ? /*#__PURE__*/_jsx(SolidCalendar, {
        "aria-hidden": "true"
      }) : /*#__PURE__*/_jsx(LineCalendar, {
        "aria-hidden": "true"
      });
    };
    $[3] = isOpen;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== excludeFromTabOrder || $[6] !== ref || $[7] !== t2 || $[8] !== t3) {
    t4 = /*#__PURE__*/_jsx(AriaButton, {
      ref: ref,
      className: t2,
      excludeFromTabOrder: excludeFromTabOrder,
      children: t3
    });
    $[5] = excludeFromTabOrder;
    $[6] = ref;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
const CalendarButton = /*#__PURE__*/React.forwardRef(BaseCalendarButton);
const BaseDateSegment = (t0, ref) => {
  const $ = _c(16);
  const {
    state,
    segment,
    size
  } = t0;
  const domRef = useObjectRef(ref);
  const {
    segmentProps
  } = useDateSegment(segment, state, domRef);
  let t1;
  if ($[0] !== segmentProps) {
    t1 = mergeProps(segmentProps);
    $[0] = segmentProps;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = segmentProps.style;
  let t3;
  if ($[2] !== segment.type || $[3] !== size) {
    t3 = styles.segment({
      size,
      type: segment.type
    });
    $[2] = segment.type;
    $[3] = size;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = segment.isPlaceholder || undefined;
  const t5 = state.isInvalid || undefined;
  const t6 = !segment.isEditable || undefined;
  const t7 = state.isDisabled || undefined;
  let t8;
  if ($[5] !== domRef || $[6] !== segment.text || $[7] !== segment.type || $[8] !== segmentProps.style || $[9] !== t1 || $[10] !== t3 || $[11] !== t4 || $[12] !== t5 || $[13] !== t6 || $[14] !== t7) {
    t8 = /*#__PURE__*/_jsx("span", {
      ...t1,
      style: t2,
      className: t3,
      ref: domRef,
      "data-placeholder": t4,
      "data-invalid": t5,
      "data-readonly": t6,
      "data-disabled": t7,
      "data-type": segment.type,
      children: segment.text
    });
    $[5] = domRef;
    $[6] = segment.text;
    $[7] = segment.type;
    $[8] = segmentProps.style;
    $[9] = t1;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
};
const DateSegment = /*#__PURE__*/React.forwardRef(BaseDateSegment);
const currentYear = new Date().getFullYear();
const startYear = currentYear - 60;
const extraYearsToShow = 1;
const undefinedMonthId = -1;
const useYearsAndMonthsValues = selectedYear => {
  const $ = _c(23);
  const i18n = useI18n();
  const addExtraYear = selectedYear != null && (selectedYear < startYear || selectedYear > currentYear + extraYearsToShow);
  const t0 = selectedYear ?? -1;
  let t1;
  if ($[0] !== selectedYear) {
    t1 = selectedYear == null ? "" : selectedYear.toString();
    $[0] = selectedYear;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = {
      id: t0,
      name: t1
    };
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const extraYear = t2;
  let years;
  if ($[5] !== addExtraYear || $[6] !== extraYear || $[7] !== selectedYear) {
    years = Array.from({
      length: currentYear - startYear + extraYearsToShow
    }).map(_temp).reverse();
    if (addExtraYear && selectedYear > startYear) {
      years = [extraYear, ...years];
    } else {
      if (selectedYear != null && selectedYear < startYear) {
        years = [...years, extraYear];
      }
    }
    $[5] = addExtraYear;
    $[6] = extraYear;
    $[7] = selectedYear;
    $[8] = years;
  } else {
    years = $[8];
  }
  const {
    locale
  } = useLocale();
  let t3;
  let undefinedValue;
  if ($[9] !== i18n || $[10] !== locale) {
    const formatter = new Intl.DateTimeFormat(locale, {
      month: "long"
    });
    let t4;
    if ($[13] !== i18n) {
      t4 = i18n.format("datePickerDialogMonthUnspecified");
      $[13] = i18n;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    let t5;
    if ($[15] !== t4) {
      t5 = {
        id: undefinedMonthId,
        name: t4
      };
      $[15] = t4;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    undefinedValue = t5;
    t3 = Array.from({
      length: 12
    }).map((__0, index) => ({
      id: index + 1,
      name: `${(index + 1).toString().padStart(2, "0")} - ${formatter.format(new Date(1992, index, 1))}`
    }));
    $[9] = i18n;
    $[10] = locale;
    $[11] = t3;
    $[12] = undefinedValue;
  } else {
    t3 = $[11];
    undefinedValue = $[12];
  }
  const months = t3;
  let t4;
  if ($[17] !== months || $[18] !== undefinedValue) {
    t4 = [undefinedValue, ...months];
    $[17] = months;
    $[18] = undefinedValue;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== t4 || $[21] !== years) {
    t5 = {
      years,
      months: t4
    };
    $[20] = t4;
    $[21] = years;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  return t5;
};
const MonthYearDialog = t0 => {
  const $ = _c(40);
  const {
    state
  } = t0;
  const i18n = useI18n();
  const {
    years,
    months
  } = useYearsAndMonthsValues(state.year);
  const [selectedTab, setSelectedTab] = React.useState("year");
  const listboxRef = React.useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [[ListBoxContext, {
      selectionMode: "single",
      autoFocus: "first",
      shouldFocusOnHover: true,
      shouldSelectOnPressUp: true,
      escapeKeyBehavior: "none"
    }], [ListStateContext, null]];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = key => {
      if (key !== "year" && key !== "month") {
        throw new Error(`Unexpected tab selection: ${key}. Expected 'year' or 'month'.`);
      }
      setSelectedTab(key);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== i18n) {
    t3 = i18n.format("datePickerDialogTabYear");
    $[2] = i18n;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = /*#__PURE__*/_jsx(Tab, {
      id: "year",
      children: t3
    });
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== i18n) {
    t5 = i18n.format("datePickerDialogTabMonth");
    $[6] = i18n;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = /*#__PURE__*/_jsx(Tab, {
      id: "month",
      children: t5
    });
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t4 || $[11] !== t6) {
    t7 = /*#__PURE__*/_jsxs(TabList, {
      children: [t4, t6]
    });
    $[10] = t4;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== i18n) {
    t8 = i18n.format("datePickerDialogYearListLabel");
    $[13] = i18n;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== state.year) {
    t9 = state.year == null ? [] : [state.year];
    $[15] = state.year;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== state) {
    t10 = value => {
      if (value === "all") {
        throw new Error("Unexpected selection of 'all' in year selection.");
      }
      const values = [...value].map(_temp2).filter(_temp3);
      if (values.length === 0) {
        state.clearSegment("year");
        return;
      } else {
        if (values.length > 1) {
          throw new Error(`Unexpected number of selected years, got ${values.join(", ")}, expected 1.`);
        }
      }
      state.setSegment("year", values[0]);
      setSelectedTab("month");
    };
    $[17] = state;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t8 || $[21] !== t9 || $[22] !== years) {
    t11 = /*#__PURE__*/_jsx(TabPanel, {
      ref: listboxRef,
      id: "year",
      children: /*#__PURE__*/_jsx(ListBox, {
        "aria-label": t8,
        items: years,
        selectedKeys: t9,
        onSelectionChange: t10,
        virtualized: false,
        children: _temp4
      })
    });
    $[19] = t10;
    $[20] = t8;
    $[21] = t9;
    $[22] = years;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== i18n) {
    t12 = i18n.format("datePickerDialogMonthListLabel");
    $[24] = i18n;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== state.month) {
    t13 = state.month == null ? [undefinedMonthId] : [state.month];
    $[26] = state.month;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== state) {
    t14 = value_0 => {
      if (value_0 === "all") {
        throw new Error("Unexpected selection of 'all' in month selection.");
      }
      const values_0 = [...value_0].map(_temp5).filter(_temp6);
      if (values_0.length === 0) {
        state.clearSegment("month");
        return;
      } else {
        if (values_0.length > 1) {
          throw new Error(`Unexpected number of selected years, got ${values_0.join(", ")}, expected 1.`);
        }
      }
      if (values_0[0] === undefinedMonthId) {
        state.clearSegment("month");
      } else {
        state.setSegment("month", values_0[0]);
      }
    };
    $[28] = state;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== months || $[31] !== t12 || $[32] !== t13 || $[33] !== t14) {
    t15 = /*#__PURE__*/_jsx(TabPanel, {
      id: "month",
      children: /*#__PURE__*/_jsx(ListBox, {
        items: months,
        "aria-label": t12,
        selectedKeys: t13,
        onSelectionChange: t14,
        virtualized: false,
        children: _temp7
      })
    });
    $[30] = months;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== selectedTab || $[36] !== t11 || $[37] !== t15 || $[38] !== t7) {
    t16 = /*#__PURE__*/_jsx(Provider, {
      values: t1,
      children: /*#__PURE__*/_jsxs(Tabs, {
        size: "s",
        type: "filled",
        fullWidth: true,
        selectedKey: selectedTab,
        onSelectionChange: t2,
        children: [t7, t11, t15]
      })
    });
    $[35] = selectedTab;
    $[36] = t11;
    $[37] = t15;
    $[38] = t7;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
};
const useDatePickerLabels = props => {
  const $ = _c(4);
  const fcLabelProps = React.useContext(FCTextFieldContext);
  const labelProps = useSlottedContext(LabelContext, props.slot);
  const t0 = props["aria-label"] ?? fcLabelProps.label;
  const t1 = props["aria-labelledby"] ?? labelProps?.id;
  let t2;
  if ($[0] !== fcLabelProps.labelRef || $[1] !== t0 || $[2] !== t1) {
    t2 = {
      ref: fcLabelProps.labelRef,
      label: t0,
      labelId: t1
    };
    $[0] = fcLabelProps.labelRef;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const BaseDatePicker = (baseProps, ref) => {
  const $ = _c(60);
  let t0;
  if ($[0] !== baseProps) {
    t0 = extractInputBuilderProps(baseProps);
    $[0] = baseProps;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    inputBuilderProps,
    restProps: props
  } = t0;
  const labelProps = useDatePickerLabels(props);
  const groupRef = useObjectRef(ref);
  const [userInputtedDateDirectly, setUserInputtedDateDirectly] = React.useState(false);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      granularity: "day",
      validationBehavior: "native",
      onOpenChange: isOpen => {
        if (isOpen) {
          setUserInputtedDateDirectly(false);
        }
      }
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const datePickerState = useDatePickerState(t1);
  let t2;
  if ($[3] !== labelProps.labelId) {
    t2 = {
      validationBehavior: "native",
      shouldForceLeadingZeros: true,
      "aria-labelledby": labelProps.labelId
    };
    $[3] = labelProps.labelId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    buttonProps,
    dialogProps
  } = useDatePicker(t2, datePickerState, groupRef);
  let t3;
  let t4;
  if ($[5] !== groupRef) {
    t4 = createFocusManager(groupRef);
    $[5] = groupRef;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  t3 = t4;
  const focusManager = t3;
  let t5;
  if ($[7] !== labelProps.ref) {
    t5 = labelProps.ref ?? {
      current: null
    };
    $[7] = labelProps.ref;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== baseProps.skipLabelClickListener || $[10] !== focusManager) {
    t6 = e => {
      if (baseProps.skipLabelClickListener === true) {
        return;
      }
      e.stopPropagation();
      focusManager.focusFirst();
    };
    $[9] = baseProps.skipLabelClickListener;
    $[10] = focusManager;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  useEvent(t5, "click", t6);
  const dateFieldState = useMonthYearDateFieldState(props);
  const inputRef = React.useRef(null);
  const fieldRef = React.useRef(null);
  let t7;
  if ($[12] !== props) {
    t7 = e_0 => {
      props.onBlur?.(e_0);
      setUserInputtedDateDirectly(false);
    };
    $[12] = props;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = e_1 => {
      if (e_1.key === "Tab" || e_1.key === "Enter") {
        return;
      }
      setUserInputtedDateDirectly(true);
    };
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== labelProps.label || $[16] !== props.name || $[17] !== props.onFocus || $[18] !== t7) {
    t9 = {
      "aria-label": labelProps.label,
      inputRef,
      name: props.name,
      onFocus: props.onFocus,
      onBlur: t7,
      onKeyDown: t8
    };
    $[15] = labelProps.label;
    $[16] = props.name;
    $[17] = props.onFocus;
    $[18] = t7;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const {
    fieldProps: dateFieldProps,
    inputProps: dateFieldInputProps
  } = useDateField(t9, dateFieldState, fieldRef);
  let t10;
  if ($[20] !== datePickerState) {
    t10 = [OverlayTriggerStateContext, datePickerState];
    $[20] = datePickerState;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== buttonProps || $[23] !== labelProps.labelId) {
    t11 = [ButtonContext, {
      ...buttonProps,
      "aria-labelledby": labelProps.labelId
    }];
    $[22] = buttonProps;
    $[23] = labelProps.labelId;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11) {
    t12 = [t10, t11];
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const t13 = props.slot;
  let t14;
  if ($[28] !== props.slot || $[29] !== userInputtedDateDirectly) {
    t14 = t15 => {
      const {
        loadingState,
        loadingRenderState,
        loadingStartAnimation
      } = t15;
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [loadingState == null ? null : /*#__PURE__*/_jsx(Spinner, {
          state: loadingState,
          renderState: loadingRenderState,
          startAnimation: loadingStartAnimation,
          size: "s",
          color: "royalBlue"
        }), /*#__PURE__*/_jsx(CalendarButton, {
          visible: loadingRenderState !== "loading",
          excludeFromTabOrder: props.slot === "from" && userInputtedDateDirectly
        })]
      });
    };
    $[28] = props.slot;
    $[29] = userInputtedDateDirectly;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const t15 = dateFieldState.isInvalid;
  const t16 = dateFieldState.isDisabled;
  let t17;
  if ($[31] !== dateFieldState) {
    t17 = dateFieldState.segments.map((segment, i) => /*#__PURE__*/_jsx(DateSegment, {
      state: dateFieldState,
      segment: segment,
      size: "m"
    }, i));
    $[31] = dateFieldState;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== dateFieldProps || $[34] !== dateFieldState.isDisabled || $[35] !== dateFieldState.isInvalid || $[36] !== t17) {
    t18 = /*#__PURE__*/_jsx(Group, {
      ref: fieldRef,
      ...dateFieldProps,
      isInvalid: t15,
      isDisabled: t16,
      className: styles.field,
      children: t17
    });
    $[33] = dateFieldProps;
    $[34] = dateFieldState.isDisabled;
    $[35] = dateFieldState.isInvalid;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== dateFieldInputProps || $[39] !== dateFieldState.monthYearTextValue) {
    t19 = /*#__PURE__*/_jsx(AriaInput, {
      ...dateFieldInputProps,
      value: dateFieldState.monthYearTextValue,
      ref: inputRef
    });
    $[38] = dateFieldInputProps;
    $[39] = dateFieldState.monthYearTextValue;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== groupRef || $[42] !== inputBuilderProps || $[43] !== props.slot || $[44] !== t14 || $[45] !== t18 || $[46] !== t19) {
    t20 = /*#__PURE__*/_jsxs(InputBuilder, {
      slot: t13,
      ref: groupRef,
      ...inputBuilderProps,
      right: t14,
      children: [t18, t19]
    });
    $[41] = groupRef;
    $[42] = inputBuilderProps;
    $[43] = props.slot;
    $[44] = t14;
    $[45] = t18;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== dateFieldState) {
    t21 = /*#__PURE__*/_jsx(MonthYearDialog, {
      state: dateFieldState
    });
    $[48] = dateFieldState;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== dialogProps || $[51] !== t21) {
    t22 = /*#__PURE__*/_jsx(Dialog, {
      ...dialogProps,
      className: styles.dialog,
      children: t21
    });
    $[50] = dialogProps;
    $[51] = t21;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== groupRef || $[54] !== t22) {
    t23 = /*#__PURE__*/_jsx(Popover, {
      trigger: "DateRange",
      width: 72,
      triggerRef: groupRef,
      placement: "bottom end",
      children: t22
    });
    $[53] = groupRef;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== t12 || $[57] !== t20 || $[58] !== t23) {
    t24 = /*#__PURE__*/_jsxs(Provider, {
      values: t12,
      children: [t20, t23]
    });
    $[56] = t12;
    $[57] = t20;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  return t24;
};
export const DatePicker = /*#__PURE__*/React.forwardRef(BaseDatePicker);
function _temp(_, i) {
  return {
    id: i + startYear,
    name: (i + startYear).toString()
  };
}
function _temp2(v) {
  return typeof v === "number" ? v : null;
}
function _temp3(v_0) {
  return v_0 !== null;
}
function _temp4(t0) {
  const {
    id,
    name
  } = t0;
  return /*#__PURE__*/_jsx(ListBoxItem, {
    id: id,
    children: name
  });
}
function _temp5(v_1) {
  return typeof v_1 === "number" ? v_1 : null;
}
function _temp6(v_2) {
  return v_2 !== null;
}
function _temp7(t0) {
  const {
    id: id_0,
    name: name_0
  } = t0;
  return /*#__PURE__*/_jsx(ListBoxItem, {
    id: id_0,
    children: name_0
  });
}
//# sourceMappingURL=DatePicker.js.map